import React, { useState, useEffect } from 'react';
import {
  X,
  ExternalLink,
  AlertTriangle,
  Shield,
  Activity,
  Globe,
  FileText,
  ChevronRight,
  Layers,
  Database,
  Clock
} from 'lucide-react';
import { api } from '../utils/api';
import { formatSafeRelativeTime } from '../utils/date-formatter';
import { SourceBadge } from './SourceBadge';
import { formatPostIdForDisplay } from '../utils/id-normalization';

interface DepthAnalysisModalProps {
  postId: string;
  depthLevel: number;
  onClose: () => void;
}

interface DepthAnalysisData {
  id: string;
  post_id: string;
  url: string;
  depth_level: number;
  parent_url?: string;
  
  // Crawl metadata
  crawled_at: string;
  status: 'pending' | 'crawling' | 'completed' | 'failed' | 'skipped';
  error_message?: string;
  
  // Crawled content
  title?: string;
  content?: string;
  description?: string;
  
  // Extracted data
  extracted_urls?: string[];
  extracted_iocs?: any[];
  images_count: number;
  links_count: number;
  
  // Analysis results
  threat_score: number;
  threat_level?: string;
  confidence_score: number;
  threat_categories?: string[];
  key_entities?: string[];
  
  // Performance metrics
  response_time?: number;
  content_length?: number;
  status_code?: number;
}

interface PostSummary {
  id: string;
  username: string;
  content: string;
  created_at: string;
  url?: string;
  threat_level: string;
  summary?: string;
  analysis_id?: string;
  source_feed?: string;
}

export function DepthAnalysisModal({ postId, depthLevel, onClose }: DepthAnalysisModalProps) {
  const [analysisData, setAnalysisData] = useState<DepthAnalysisData[]>([]);
  const [postSummary, setPostSummary] = useState<PostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'urls' | 'threats' | 'iocs'>('overview');

  console.log(`[DepthAnalysisModal:76] Component mounted with postId=${postId}, depthLevel=${depthLevel}`);

  useEffect(() => {
    console.log(`[DepthAnalysisModal:79] useEffect triggered for postId=${postId}, depthLevel=${depthLevel}`);
    loadAnalysisData();
  }, [postId, depthLevel]);

  const loadAnalysisData = async () => {
    console.log(`[DepthAnalysisModal:88] loadAnalysisData started for postId=${postId}, depthLevel=${depthLevel}`);
    try {
      setLoading(true);
      setError(null);
      console.log(`[DepthAnalysisModal:92] Loading state set to true`);
      
      // Load post summary
      console.log(`[DepthAnalysisModal:95] Calling api.getPostDetails for postId=${postId}`);
      const postResponse = await api.getPostDetails(postId);
      setPostSummary(postResponse);
      console.log(`[DepthAnalysisModal:98] Post summary loaded:`, { username: postResponse?.username, source_feed: postResponse?.source_feed });
      
      // Load depth analysis data
      console.log(`[DepthAnalysisModal:101] Calling api.getDepthAnalysis for postId=${postId}, depthLevel=${depthLevel}`);
      const analysisResponse = await api.getDepthAnalysis(postId, depthLevel);
      const analyses = analysisResponse.analyses || [];
      setAnalysisData(analyses);
      console.log(`[DepthAnalysisModal:105] Depth analysis data loaded: ${analyses.length} analyses`);
    } catch (err) {
      console.error(`[DepthAnalysisModal:107] Failed to load depth analysis for postId=${postId}, depthLevel=${depthLevel}:`, err);
      const errorMessage = `Failed to load analysis: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMessage);
      console.log(`[DepthAnalysisModal:110] Error state set:`, errorMessage);
    } finally {
      setLoading(false);
      console.log(`[DepthAnalysisModal:113] Loading state set to false`);
    }
  };

  const getDepthLevelName = (level: number) => {
    switch (level) {
      case 0: return 'Original Post';
      case 1: return 'Level 1 - Direct URLs';
      case 2: return 'Level 2 - Nested URLs';
      case 3: return 'Level 3 - Deep Analysis';
      default: return `Level ${level}`;
    }
  };

  const getDepthLevelDescription = (level: number) => {
    switch (level) {
      case 0: return 'Analysis of the original social media post content';
      case 1: return 'Analysis of URLs found directly in the post';
      case 2: return 'Analysis of URLs found within Level 1 pages';
      case 3: return 'Deep analysis of URLs found within Level 2 pages';
      default: return `Analysis at depth level ${level}`;
    }
  };

  const renderOverview = () => {
    if (analysisData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Layers className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No analysis data available for this depth level</p>
        </div>
      );
    }

    const completedAnalyses = analysisData.filter(a => a.status === 'completed');
    const criticalThreats = completedAnalyses.filter(a => a.threat_level === 'critical');
    const highThreats = completedAnalyses.filter(a => a.threat_level === 'high');
    const totalIOCs = analysisData.reduce((sum, a) => sum + (a.extracted_iocs?.length || 0), 0);

    return (
      <div className="space-y-4">
        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{analysisData.length}</div>
            <div className="text-sm text-gray-600">Total URLs</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-900">{completedAnalyses.length}</div>
            <div className="text-sm text-green-600">Analyzed</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-900">{criticalThreats.length}</div>
            <div className="text-sm text-red-600">Critical Threats</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-900">{highThreats.length}</div>
            <div className="text-sm text-orange-600">High Threats</div>
          </div>
        </div>

        {/* Top Threats */}
        {(criticalThreats.length > 0 || highThreats.length > 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-medium text-red-900 mb-3 flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>High Priority Threats</span>
            </h3>
            <div className="space-y-2">
              {[...criticalThreats, ...highThreats].slice(0, 5).map((threat, idx) => (
                <div key={threat.id} className="bg-white p-3 rounded border border-red-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{threat.title || threat.url}</div>
                      <div className="text-sm text-gray-600 mt-1">{threat.description || 'No description available'}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      threat.threat_level === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {threat.threat_level?.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-3">Analysis Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total IOCs Extracted:</span>
              <span className="font-medium">{totalIOCs}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Threat Score:</span>
              <span className="font-medium">
                {(completedAnalyses.reduce((sum, a) => sum + a.threat_score, 0) / completedAnalyses.length || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Success Rate:</span>
              <span className="font-medium">{((completedAnalyses.length / analysisData.length) * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUrls = () => {
    return (
      <div className="space-y-3">
        {analysisData.map((analysis) => (
          <div key={analysis.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <a
                  href={analysis.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center space-x-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="break-all">{analysis.url}</span>
                </a>
                {analysis.parent_url && (
                  <div className="text-sm text-gray-600 mt-1">
                    Found in: <span className="font-mono text-xs">{analysis.parent_url}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {analysis.status === 'completed' && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    analysis.threat_level === 'critical' ? 'bg-red-100 text-red-800' :
                    analysis.threat_level === 'high' ? 'bg-orange-100 text-orange-800' :
                    analysis.threat_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    analysis.threat_level === 'low' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {analysis.threat_level?.toUpperCase() || 'NONE'}
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                  analysis.status === 'failed' ? 'bg-red-100 text-red-800' :
                  analysis.status === 'skipped' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {analysis.status.toUpperCase()}
                </span>
              </div>
            </div>

            {analysis.title && (
              <h4 className="font-medium text-gray-900 mb-1">{analysis.title}</h4>
            )}
            
            {analysis.description && (
              <p className="text-sm text-gray-600 mb-2">{analysis.description}</p>
            )}

            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{formatSafeRelativeTime(analysis.crawled_at, { addSuffix: true })}</span>
              </span>
              {analysis.response_time && (
                <span>{analysis.response_time}ms</span>
              )}
              {analysis.status_code && (
                <span>Status: {analysis.status_code}</span>
              )}
              {analysis.links_count > 0 && (
                <span>{analysis.links_count} links</span>
              )}
            </div>

            {analysis.threat_categories && analysis.threat_categories.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {analysis.threat_categories.map((category, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                    {category}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderThreats = () => {
    const threatsData = analysisData
      .filter(a => a.status === 'completed' && a.threat_score > 0)
      .sort((a, b) => b.threat_score - a.threat_score);

    if (threatsData.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No threats detected at this depth level</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {threatsData.map((threat) => (
          <div key={threat.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-medium text-gray-900">{threat.title || threat.url}</h4>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Score:</span>
                <span className="font-bold text-lg">{(threat.threat_score * 100).toFixed(0)}%</span>
              </div>
            </div>
            
            {threat.content && (
              <p className="text-sm text-gray-700 mb-2 line-clamp-3">{threat.content}</p>
            )}

            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {threat.threat_categories?.map((category, idx) => (
                  <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                    {category}
                  </span>
                ))}
              </div>
              <a
                href={threat.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1"
              >
                <span>View Source</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderIOCs = () => {
    const allIOCs: any[] = [];
    
    analysisData.forEach(analysis => {
      if (analysis.extracted_iocs && analysis.extracted_iocs.length > 0) {
        analysis.extracted_iocs.forEach(ioc => {
          allIOCs.push({
            ...ioc,
            source_url: analysis.url,
            source_title: analysis.title
          });
        });
      }
    });

    if (allIOCs.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Database className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No IOCs extracted at this depth level</p>
        </div>
      );
    }

    // Group IOCs by type
    const iocsByType = allIOCs.reduce((acc, ioc) => {
      const type = ioc.type || 'unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(ioc);
      return acc;
    }, {} as Record<string, any[]>);

    return (
      <div className="space-y-4">
        {Object.entries(iocsByType).map(([type, iocs]) => (
          <div key={type} className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3 capitalize">{type.replace('_', ' ')} IOCs</h4>
            <div className="space-y-2">
              {iocs.map((ioc, idx) => (
                <div key={idx} className="bg-white p-3 rounded border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <code className="text-sm bg-gray-800 text-gray-100 px-2 py-1 rounded">
                        {ioc.value}
                      </code>
                      {ioc.source_title && (
                        <div className="text-xs text-gray-600 mt-1">
                          From: {ioc.source_title}
                        </div>
                      )}
                    </div>
                    {ioc.confidence && (
                      <span className="text-sm text-gray-600">
                        {Math.round(ioc.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'urls': return renderUrls();
      case 'threats': return renderThreats();
      case 'iocs': return renderIOCs();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading depth analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">Depth Analysis - {getDepthLevelName(depthLevel)}</h2>
              {postSummary?.source_feed && (
                <SourceBadge source_feed={postSummary.source_feed} username={postSummary.username} size="md" />
              )}
            </div>
            <p className="text-sm text-gray-600">{getDepthLevelDescription(depthLevel)}</p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span className="font-mono text-xs bg-blue-100 px-2 py-1 rounded text-blue-700">
                Post: {formatPostIdForDisplay(postId)}
              </span>
              <span className="font-mono text-xs bg-purple-100 px-2 py-1 rounded text-purple-700">
                Depth Level: {depthLevel}
              </span>
              <span>•</span>
              <span>{analysisData.length} URLs analyzed</span>
              <span>•</span>
              <span>@{postSummary?.username}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity className="w-4 h-4" />
              <span>Overview</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('urls')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'urls'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>Analyzed URLs</span>
              {analysisData.length > 0 && (
                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                  {analysisData.length}
                </span>
              )}
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('threats')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'threats'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Threats</span>
              {analysisData.filter(a => a.threat_score > 0.5).length > 0 && (
                <span className="bg-red-200 text-red-700 px-2 py-0.5 rounded-full text-xs">
                  {analysisData.filter(a => a.threat_score > 0.5).length}
                </span>
              )}
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('iocs')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'iocs'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Database className="w-4 h-4" />
              <span>IOCs</span>
              {analysisData.reduce((sum, a) => sum + (a.extracted_iocs?.length || 0), 0) > 0 && (
                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs">
                  {analysisData.reduce((sum, a) => sum + (a.extracted_iocs?.length || 0), 0)}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="flex items-center space-x-4">
            {postSummary?.url && (
              <a
                href={postSummary.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="w-4 h-4" />
                <span>View Original Post</span>
              </a>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}